import { useSdk } from "@fondationcss/sdk";
import type { FormSchema, IFetchWrapper } from "@fondationcss/sdk";
import type {
    CourseCodeRequest,
    CourseCodeResponse, 
    CourseCodesResponse, 
    CourseLevelRequest, 
    CourseLevelResponse, 
    CourseLevelsResponse,
    CourseThemesResponse,
    TimelineRequest,
    TimelineResponse,
    TimeLinesResponse,
    TimeLineVisibilityRequest,
    ImportDataResponseDto
} from "./dto";
import type { CourseCode, CourseLevel } from "./model";
import type {
    AdmissionStatus,
    AdmissionDto,
    AdmissionDetail,
    AdmissionNoteDto,
    AdmissionFollowUpDto,
    AdmissionCommunicationDto
} from "./model/admission";
import type { FgaEnrollmentDto, FgaEnrollmentDetail } from "./model/fga-enrollment";
import type { FgaImportJobDto } from "./model/fga-import";
import type { StudentDto, StudentSummaryDto, UpdateStudentRequest } from "./model/student";
import type { GricsCentreImportReport, GricsEmployeeImportReport, SiteInput } from "@byrnu/agora-shared";
import type { CentreImportResult, PerspectiveSite } from "./model/site";
import type { PerspectiveTeacher } from "./model/teacher";

// Base URL for calls that bypass fetchWrapper's auth interceptor (raw fetch/EventSource) — same
// fallback the public-admission functions below use, hoisted here so it's reusable without a
// running getCustomSdk() instance.
export const publicApiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Per-call request options (e.g. a custom errorHandler) forwarded to the underlying fetch wrapper. */
export type SdkRequestOptions = NonNullable<Parameters<IFetchWrapper['post']>[2]>;

export interface TimeLineApi {
    get(id: string): Promise<TimelineResponse>;
    list(includeArchived?: boolean): Promise<TimeLinesResponse>;
    getMyRecent(): Promise<TimeLinesResponse>;
    create(data: TimelineRequest): Promise<TimelineResponse>;
    update(data: TimelineRequest): Promise<unknown>;
    updateVisibility(id: string, request: TimeLineVisibilityRequest): Promise<unknown>;
    delete(id: string): Promise<unknown>;
    archive(id: string): Promise<unknown>;
    restore(id: string): Promise<unknown>;
    copy(id: string): Promise<TimelineResponse>;
}

export const getCustomSdk = () => {
    const sdk = useSdk();

    // TimeLine — two implementations of the same interface (global vs. student-scoped).
    const makeTimeLineApi = (basePath: string): TimeLineApi => ({
        get:               (id)            => sdk.fetchWrapper.get<TimelineResponse>(`${basePath}/${id}`),
        list:              (includeArchived = false) => sdk.fetchWrapper.get<TimeLinesResponse>(`${basePath}/List${includeArchived ? "?includeArchived=true" : ""}`),
        getMyRecent:       ()              => sdk.fetchWrapper.get<TimeLinesResponse>(`${basePath}/MyRecent`),
        create:            (data)          => sdk.fetchWrapper.post<TimelineResponse>(`${basePath}/`, data),
        update:            (data)          => sdk.fetchWrapper.put(`${basePath}/`, data),
        updateVisibility:  (id, request)   => sdk.fetchWrapper.put(`${basePath}/${id}/Visibility`, request),
        delete:            (id)            => sdk.fetchWrapper.delete(`${basePath}/${id}`),
        archive:           (id)            => sdk.fetchWrapper.get(`${basePath}/${id}/Archive`),
        restore:           (id)            => sdk.fetchWrapper.get(`${basePath}/${id}/Restore`),
        copy:              (id)            => sdk.fetchWrapper.post<TimelineResponse>(`${basePath}/${id}/Copy`, {}),
    });

    const globalTimeLineApi: TimeLineApi = makeTimeLineApi("/TimeLine");
    // /students/me resolves the student id from the current user server-side, so the frontend doesn't need the user's Guid.
    const myStudentTimeLineApi: TimeLineApi = makeTimeLineApi("/students/me/TimeLine");

    // Auto-pick: a user holding ONLY the Student role (no staff role) is a student and must
    // go through the student-scoped /me routes. Anyone else gets the global API.
    const isStudentOnly = (): boolean => {
        const permissions = sdk.me.value?.permissions || [];
        return permissions.includes("Perspective.TimeLine.ManageOwn")
            && !permissions.includes("Perspective.TimeLine.View")
            && !permissions.includes("Perspective.TimeLine.Manage");
    };

    // Admin-only: list timelines targeting a specific user. Has no student-scoped equivalent, so it lives outside the interface.
    const listTimeLinesForUser = async (email: string) => await sdk.fetchWrapper.get<TimeLinesResponse>(`/TimeLine/List/${email}`);

    // Catalogue de cours (/api/Course/*) — lectures : Perspective.Course.Read (tous les
    // rôles) ; mutations : Perspective.Course.Manage.
    const getCourseLevel = async (id: string)                   => await sdk.fetchWrapper.get<CourseLevelResponse>(`/Course/Level/${id}`);
    const getCourseLevels = async (includeArchived = false)     => await sdk.fetchWrapper.get<CourseLevelsResponse>(`/Course/Level${includeArchived ? "?includeArchived=true" : ""}`);
    const addCourseLevel = async (data: CourseLevelRequest)     => await sdk.fetchWrapper.post<CourseLevelResponse>("/Course/Level", data);
    const updateCourseLevel = async (data: CourseLevel)         => await sdk.fetchWrapper.put("/Course/Level", data);
    const deleteCourseLevel = async (id: string)                => await sdk.fetchWrapper.delete(`/Course/Level/${id}`);
    const restoreCourseLevel = async (id: string)               => await sdk.fetchWrapper.get(`/Course/Level/${id}/Restore`);
    const getCourseCode = async (id: string)                    => await sdk.fetchWrapper.get<CourseCodeResponse>(`/Course/Code/${id}`);
    const getCourseCodes = async ()                             => await sdk.fetchWrapper.get<CourseCodesResponse>("/Course/Code");
    const addCourseCode = async (data: CourseCodeRequest)       => await sdk.fetchWrapper.post<CourseCodeResponse>("/Course/Code", data);
    const updateCourseCode = async (data: CourseCode)           => await sdk.fetchWrapper.put("/Course/Code", data);
    const archiveCourseCode = async (id: string)                => await sdk.fetchWrapper.get(`/Course/Code/${id}/Archive`);
    const restoreCourseCode = async (id: string)                => await sdk.fetchWrapper.get(`/Course/Code/${id}/Restore`);
    const getCourseThemes = async ()                            => await sdk.fetchWrapper.get<CourseThemesResponse>("/Course/Theme");

    // Rapports PDF (/api/Report/*). Chaque rapport est gardé par les permissions de son sujet :
    // le calendrier scolaire relève de Core.Calendar (View ou Manage), pas d'une permission de
    // rapport à part — voir ReportModule côté backend.
    const generateCalendarYearReport = async (calendarId: string, calendarYearId: string): Promise<Blob> =>
        await sdk.fetchWrapper.downloadPost("/Report/CalendarYear", { calendarId, calendarYearId });

    // Admission publique (anonyme) — endpoints hors auth /api/public/admission/*.
    // La page publique n'initialise pas le SDK authentifié (donc pas de base URL via
    // settings) : on appelle l'API directement avec la base configurée au build.
    const getPublicAdmissionSchema = async (): Promise<FormSchema> => {
        const response = await fetch(`${publicApiBase}/public/admission/schema`, { credentials: "include" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    };
    const submitPublicAdmission = async (payload: {
        values: Record<string, unknown>;
        consentAccepted: boolean;
        /** Identifiant du document juridique affiché, quand un avis est publié (#115). */
        consentLegalDocumentId?: string;
        /** Repli sans avis publié : version et empreinte SHA-256 du texte intégré affiché. */
        consentEmbeddedNoticeVersion?: string;
        consentEmbeddedNoticeHash?: string;
        phoneNumber?: string;
    }): Promise<{ admissionId: string }> => {
        const response = await fetch(`${publicApiBase}/public/admission/submit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
    };
    // Candidat authentifié : reprise de sa demande, courriel confirmé.
    const getMyAdmission = async (): Promise<{
        email: string | null; admissionId?: string; status?: string; editable?: boolean;
        cellPhoneNumber?: string; values?: Record<string, unknown>;
    }> => {
        const r = await fetch(`${publicApiBase}/public/admission/mine`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await r.json();
    };
    // Paramètres d'affichage du formulaire d'admission (instructions + logo) — public.
    const getPublicAdmissionSettings = async (): Promise<{ instructionsMarkdown: string; logoUrl: string }> => {
        const r = await fetch(`${publicApiBase}/public/admission/settings`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await r.json();
    };
    // Avis de collecte Loi 25 courant (Core.Privacy), endpoint anonyme /api/Privacy/Document.
    // Source unique de vérité de l'avis affiché avant le formulaire public. Retourne null si
    // aucun avis n'est publié (l'appelant retombe alors sur l'avis intégré au build).
    const getPublicPrivacyNotice = async (): Promise<{ id: string; bodyMarkdown: string; version: number } | null> => {
        try {
            const r = await fetch(`${publicApiBase}/Privacy/Document?type=PrivacyNotice`, { credentials: "include" });
            if (!r.ok) return null;
            const doc = await r.json();
            if (!doc || typeof doc.bodyMarkdown !== "string") return null;
            // L'identifiant est renvoyé au moment du consentement pour que la version acceptée
            // soit rattachée au document réel plutôt qu'à une chaîne libre (#115).
            return { id: doc.id, bodyMarkdown: doc.bodyMarkdown, version: doc.version };
        } catch {
            return null;
        }
    };

    // Démarre l'authentification CourierID pour l'inscription, retour sur /inscription.
    const startCourierIdInscription = () => {
        const returnUrl = `${window.location.origin}/inscription`;
        window.location.href = `${publicApiBase}/core/auth/oidc/courierid/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    };

    // Admission — révision interne (rôle Perspective.Admissions.Reviewer)
    const listAdmissions = (status?: AdmissionStatus) =>
        sdk.fetchWrapper.get<{ admissions: AdmissionDto[] }>(`/admissions${status ? `?status=${status}` : ""}`);
    const getAdmission = (id: string) =>
        sdk.fetchWrapper.get<AdmissionDetail>(`/admissions/${id}`);
    const updateAdmissionStatus = (id: string, status: AdmissionStatus) =>
        sdk.fetchWrapper.patch<AdmissionDto>(`/admissions/${id}/status`, { status });
    const updateAdmissionValues = (id: string, values: Record<string, unknown>) =>
        sdk.fetchWrapper.patch(`/admissions/${id}/values`, { values });
    const addAdmissionNote = (id: string, body: string) =>
        sdk.fetchWrapper.post<{ note: AdmissionNoteDto }>(`/admissions/${id}/notes`, { body });
    const addAdmissionFollowUp = (id: string, payload: { dueDate: string; subject: string; notes: string }) =>
        sdk.fetchWrapper.post<{ followUp: AdmissionFollowUpDto }>(`/admissions/${id}/followups`, payload);
    const completeAdmissionFollowUp = (followUpId: string) =>
        sdk.fetchWrapper.patch<{ followUp: AdmissionFollowUpDto }>(`/admissions/followups/${followUpId}/complete`, {});
    const sendAdmissionEmail = (id: string, payload: { recipient?: string; subject: string; body: string }) =>
        sdk.fetchWrapper.post<{ communication: AdmissionCommunicationDto }>(`/admissions/${id}/communications/email`, payload);
    const sendAdmissionSms = (id: string, payload: { recipient: string; body: string }) =>
        sdk.fetchWrapper.post<{ communication: AdmissionCommunicationDto }>(`/admissions/${id}/communications/sms`, payload);
    const convertAdmission = (id: string) =>
        sdk.fetchWrapper.post<{ studentId: string }>(`/admissions/${id}/convert`, {});
    const getAdmissionFormStatus = () =>
        sdk.fetchWrapper.get<{ exists: boolean }>(`/admissions/form`);
    const createAdmissionForm = () =>
        sdk.fetchWrapper.post<{ exists: boolean }>(`/admissions/form`, {});
    const deleteAdmissionForm = () =>
        sdk.fetchWrapper.delete<{ exists: boolean }>(`/admissions/form`);
    const getAdmissionSettings = () =>
        sdk.fetchWrapper.get<{ instructionsMarkdown: string; logoUrl: string }>(`/admissions/settings`);
    const updateAdmissionSettings = (payload: { instructionsMarkdown: string; logoUrl: string }) =>
        sdk.fetchWrapper.put<{ instructionsMarkdown: string; logoUrl: string }>(`/admissions/settings`, payload);

    // Fréquentations FGA (GRICS)
    const listFgaEnrollments = () =>
        sdk.fetchWrapper.get<{ enrollments: FgaEnrollmentDto[] }>(`/fga-enrollments`);
    const getFgaEnrollment = (id: string) =>
        sdk.fetchWrapper.get<FgaEnrollmentDetail>(`/fga-enrollments/${id}`);

    // Parcours élève (Perspective.Students) — vue/édition des coordonnées d'un élève. Liste
    // complète, comme listFgaEnrollments/listAdmissions : recherche/tri/pagination sont
    // appliqués côté client par ServerDataGrid (volume d'un seul organisme).
    const listStudents = () =>
        sdk.fetchWrapper.get<{ students: StudentSummaryDto[] }>(`/students`);
    const getStudent = (id: string) =>
        sdk.fetchWrapper.get<StudentDto>(`/students/${id}`);
    const updateStudent = (id: string, payload: UpdateStudentRequest) =>
        sdk.fetchWrapper.put<StudentDto>(`/students/${id}`, payload);

    // Centres (Perspective.Sites). Writes take the caller's options: the shared SiteManagement
    // passes { errorHandler: null } and shows the server's refusal itself.
    const listSites = (includeArchived = false, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ sites: PerspectiveSite[] }>(`/sites${includeArchived ? "?includeArchived=true" : ""}`, options);
    const createSite = (payload: SiteInput, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<PerspectiveSite>(`/sites`, payload, options);
    const updateSite = (id: string, payload: SiteInput, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/sites/${id}`, payload, options);
    const archiveSite = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/sites/${id}/archive`, {}, options);
    const restoreSite = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/sites/${id}/restore`, {}, options);
    const setSiteDefaultSchedule = (id: string, scheduleAssignmentChainId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/sites/${id}/default-schedule`, { scheduleAssignmentChainId }, options);
    const clearSiteDefaultSchedule = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/sites/${id}/default-schedule`, options);
    const getSitesGricsAvailability = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ configured: boolean }>(`/sites/grics-availability`, options);
    const previewSitesGricsImport = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<GricsCentreImportReport>(`/sites/grics-import/preview`, {}, options);
    const runSitesGricsImport = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<CentreImportResult>(`/sites/grics-import/run`, {}, options);

    // Enseignants (Perspective.Teachers.Manage). Writes take the caller's options: the shared
    // TeacherManagement passes { errorHandler: null } and shows the server's refusal itself.
    const listTeachers = (includeArchived = false, options?: SdkRequestOptions) =>
        // i18n-ignore-next-line: query string of the API route, not text
        sdk.fetchWrapper.get<{ teachers: PerspectiveTeacher[] }>(`/teachers${includeArchived ? "?includeArchived=true" : ""}`, options);
    const getTeacher = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<PerspectiveTeacher>(`/teachers/${id}`, options);
    const addTeacher = (userId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<PerspectiveTeacher>(`/teachers`, { userId }, options);
    const addTeachersFromGroup = (groupId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ teachers: PerspectiveTeacher[] }>(`/teachers/from-group`, { groupId }, options);
    const archiveTeacher = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/teachers/${id}/archive`, {}, options);
    const restoreTeacher = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<void>(`/teachers/${id}/restore`, {}, options);
    const setTeacherColor = (id: string, color: string | null, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/teachers/${id}/color`, { color }, options);
    const getTeachersEntraMapping = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ isMappedToEntra: boolean }>(`/teachers/entra-mapping`, options);
    const getTeachersGricsAvailability = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.get<{ configured: boolean }>(`/teachers/grics-availability`, options);
    const previewTeachersGricsImport = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<GricsEmployeeImportReport>(`/teachers/grics-import/preview`, {}, options);
    const runTeachersGricsImport = (options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<GricsEmployeeImportReport>(`/teachers/grics-import/run`, {}, options);

    // The student's own schedule (Perspective.Students.Manage): a chain created through Fondation,
    // attached or let go of here.
    const setStudentSchedule = (id: string, scheduleAssignmentChainId: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.put<void>(`/students/${id}/schedule`, { scheduleAssignmentChainId }, options);
    const clearStudentSchedule = (id: string, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.delete<void>(`/students/${id}/schedule`, options);

    // Import FGA (GRICS)
    const startFgaImport = (enrollmentStartAfter?: string | null, options?: SdkRequestOptions) =>
        sdk.fetchWrapper.post<{ jobId: string }>(`/fga-import/run`, { enrollmentStartAfter: enrollmentStartAfter || null }, options);
    const getFgaImportJob = (jobId: string, options?: SdkRequestOptions) => sdk.fetchWrapper.get<FgaImportJobDto>(`/fga-import/${jobId}`, options);
    const getFgaImportStreamToken = (options?: SdkRequestOptions) => sdk.fetchWrapper.get<{ token: string }>(`/fga-import/stream-token`, options);
    const cancelFgaImport = (jobId: string, options?: SdkRequestOptions) => sdk.fetchWrapper.post<void>(`/fga-import/${jobId}/cancel`, {}, options);

    // Import/Export
    const exportData = async (): Promise<Blob>                  => {
        try {
            // Use SDK's built-in download method
            return await sdk.fetchWrapper.download("/Course/Export");
        } catch (error) {
            console.error('Export error:', error);
            throw error;
        }
    };
    const importData = async (file: File): Promise<ImportDataResponseDto> => {
        try {
            // Use SDK's built-in upload method for file uploads
            const response = await sdk.fetchWrapper.upload("/Course/Import", file);
            return response as ImportDataResponseDto;
        } catch (error) {
            console.error('Import error:', error);
            throw error;
        }
    };

    return {

        // TimeLines — interface-based access. `timeLineApi` auto-picks the right impl based on the
        // current user's roles (student-only → /students/me/TimeLine; otherwise → /TimeLine).
        get timeLineApi(): TimeLineApi { return isStudentOnly() ? myStudentTimeLineApi : globalTimeLineApi; },

        // TimeLines — admin-only outlier (no student-scoped equivalent)
        listTimeLinesForUser,

        // Catalogue de cours — lectures : Course.Read (tous les rôles) ; mutations : Course.Manage
        getCourseLevel,
        getCourseLevels,
        addCourseLevel,
        updateCourseLevel,
        deleteCourseLevel,
        restoreCourseLevel,
        getCourseCode,
        getCourseCodes,
        addCourseCode,
        updateCourseCode,
        archiveCourseCode,
        restoreCourseCode,
        getCourseThemes,

        // Rapports PDF
        generateCalendarYearReport,

        // Admission publique
        getPublicAdmissionSchema,
        submitPublicAdmission,
        getMyAdmission,
        startCourierIdInscription,
        getPublicAdmissionSettings,
        getPublicPrivacyNotice,

        // Admission — révision interne
        listAdmissions,
        getAdmission,
        updateAdmissionStatus,
        updateAdmissionValues,
        addAdmissionNote,
        addAdmissionFollowUp,
        completeAdmissionFollowUp,
        sendAdmissionEmail,
        sendAdmissionSms,
        convertAdmission,
        getAdmissionFormStatus,
        createAdmissionForm,
        deleteAdmissionForm,
        getAdmissionSettings,
        updateAdmissionSettings,

        // Parcours élève (Perspective.Students)
        listStudents,
        getStudent,
        updateStudent,
        setStudentSchedule,
        clearStudentSchedule,

        // Centres (Perspective.Sites)
        listSites,
        createSite,
        updateSite,
        archiveSite,
        restoreSite,
        setSiteDefaultSchedule,
        clearSiteDefaultSchedule,
        getSitesGricsAvailability,
        previewSitesGricsImport,
        runSitesGricsImport,

        // Enseignants (Perspective.Teachers)
        listTeachers,
        getTeacher,
        addTeacher,
        addTeachersFromGroup,
        archiveTeacher,
        restoreTeacher,
        setTeacherColor,
        getTeachersEntraMapping,
        getTeachersGricsAvailability,
        previewTeachersGricsImport,
        runTeachersGricsImport,

        // Fréquentations FGA (GRICS)
        listFgaEnrollments,
        getFgaEnrollment,

        // Import FGA (GRICS)
        startFgaImport,
        getFgaImportJob,
        getFgaImportStreamToken,
        cancelFgaImport,

        // Import/Export
        exportData,
        importData
    };
}